import { DetailCard, DetailCardHead } from "@/components/ui/detail-card";
import "./box.css";

import * as React from "react";
import { createPortal } from "react-dom";
import { VisitTaskFoot } from "./visit-task-foot";
import { Link } from "react-router";

import {
  assertPage,
  blockerSentence,
  blockersOf,
  foldedResult,
  leadOf,
  openStateOf,
  restsSettled,
  scopeSentence,
  stateOf,
  type Blocker,
  type Box,
  type BoxState,
  type BoxView,
  type BulkScope,
  type Candidate,
  type Commit,
  type Exit,
  type FaceAct,
  type FactBox as FactBoxSpec,
  type FactField,
  type FactOwner,
  type FieldAria,
  type FieldSave,
  type Figure,
  type ListBox as ListBoxSpec,
  type PageRefusal as PageRefusalSpec,
  type Part,
  type Read,
  type Result,
  type RowAct,
  type RowTask,
  type StateClass,
  type TaskBox as TaskBoxSpec,
  type VisitAddress,
} from "@/components/ui/box.schema";
import { BentoVisit, type BentoVisitSlot } from "@/components/ui/box-visit-context";
import { Button } from "@/components/ui/button";
import { ConflictNotice } from "@/components/ui/conflict-notice";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Register, RegisterCode, RegisterMark, RegisterRow } from "@/components/ui/register";
import { SaveStatus } from "@/components/ui/save-status";
import { TileHeader } from "@/components/ui/tile";
import { useTileHeadingLevel } from "@/components/ui/tile-heading-context";
import { EditionCover } from "@/components/workspace/EditionCover";
import { cn } from "@/lib/utils";

/**
 * The bento box primitive (CONTRACT v1.0): `Bento` is the page, and
 * `FactBox`, `ListBox` and `TaskBox` are the three kinds it draws. Every
 * attribute in CONTRACT §Hooks is emitted here, by the component that draws
 * it, from the box's own fields; no prop lets a consumer set one.
 *
 * A box answers before it asks (F1): its face is its label, its state as the
 * headline, one detail line and at most one act. A settled box rests as one
 * line (F3). Opened, it takes its row (O1): its parts come in time order (O2)
 * and its foot holds the effects, the named choices, the blockers and one
 * commit (O3), kept in view (O4). A commit's result stays in the box and takes
 * focus (W1, W2). The page grants at most one lead, and the person's choice
 * owns it (P2, P3).
 */

/* ------------------------------------------------------------------ */
/* The page                                                            */
/* ------------------------------------------------------------------ */

type FocusTarget = "face" | "head" | "result" | { part: string } | { row: string };

interface BentoContextValue {
  prefix: string;
  lead: string | null;
  intent: string | null;
  isOpen(box: Box): boolean;
  isRowOpen(box: Box, key: string, startsOpen: boolean): boolean;
  open(id: string): void;
  hide(id: string): void;
  complete(id: string): void;
  toggleRow(id: string, key: string, open: boolean): void;
  /** The person changed a value in this box: it takes intent without opening anything (O8, P3). */
  intend(id: string): void;
  /** The person changed a Fact's field: no intent, since a field save isn't an act (CASE-026), but outside a visit it is new work (O10). */
  edit(id: string): void;
  /** Whether a commit is in flight, by box and foot; it outlives a hidden foot (O8, W1). */
  isPending(key: string): boolean;
  setPending(key: string, pending: boolean): void;
  /** While a visit is open: its origin, and the boxes it brought, the only ones that rank (O10, P2); on a page that doesn't draw it, none of the page's boxes (CASE-046). */
  visit: { at: string; ids: ReadonlySet<string> } | null;
  /** The zone the page says its times in. */
  zone: string | undefined;
  /** The page's declared pair, by box id: the other List and this one's share of the row (CASE-048). */
  pair: ReadonlyMap<string, { with: string; span: 7 | 5 }>;
}

const BentoContext = React.createContext<BentoContextValue | null>(null);

/** The press of a visit act, or null when no host can take it. */
function useVisitPress(address: VisitAddress, box: string, next?: true): (() => void) | null {
  const host = React.useContext(BentoVisit);
  if (host) return () => host.visit(address, next ? { box, next } : { box });
  if (import.meta.env.DEV) throw new Error("A visit act needs a BentoVisit host.");
  // Never a page load: without a host, the act is drawn and does nothing (F4).
  return null;
}

function useBento(): BentoContextValue {
  const context = React.useContext(BentoContext);
  if (!context) throw new Error("A box is drawn only inside a Bento page.");
  return context;
}

/** A provider's name never breaks across lines. */
const OWNER_NAMES: Record<Exclude<FactOwner["system"], "imam">, string> = {
  classroom: "Classroom", drive: "Drive", zoom: "Zoom", gmail: "Gmail", google_chat: "Google\u00a0Chat",
};

/**
 * A provider's record names its owner and, once read, when it was read: the
 * box says it once, here, and never on each row (C4, C5). Unread is the name
 * alone, never a time made up.
 */
function ownerName(owner: FactOwner, zone: string | undefined): string | null {
  if (owner.system === "imam") return null;
  return owner.readAt ? `${OWNER_NAMES[owner.system]}\u00a0· read ${readTime(owner.readAt, zone)}` : OWNER_NAMES[owner.system];
}

/** The owner chip's line: a provider's name and read time, or an IMAM record's read time where its box opts in (CASE-039). */
function ownerLine(box: Box, zone: string | undefined): string | null {
  if (box.owner.system !== "imam") return ownerName(box.owner, zone);
  return box.readTime && box.read.status === "ready" ? `Read ${readTime(box.read.readAt, zone)}` : null;
}

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/**
 * A read time in the kit's words, from the ISO instant an area passes:
 * "Today, 9:12 AM", or "Tue, Oct 20, 6:26 PM" on another day, in the page's
 * zone (CASE-039). A string that isn't an instant is said as it was given.
 */
function readTime(value: string, zone: string | undefined, now = new Date()): string {
  const at = new Date(value);
  if (!ISO_INSTANT.test(value) || Number.isNaN(at.getTime())) return wholeTimes(value);
  const parts = (date: Date) => {
    try {
      return new Intl.DateTimeFormat("en-US", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
    } catch {
      return new Intl.DateTimeFormat("en-US", { year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
    }
  };
  const say = (options: Intl.DateTimeFormatOptions) => {
    try {
      return new Intl.DateTimeFormat("en-US", { ...options, timeZone: zone }).format(at);
    } catch {
      return new Intl.DateTimeFormat("en-US", options).format(at);
    }
  };
  const [day, today] = [parts(at), parts(now)];
  const time = say({ hour: "numeric", minute: "2-digit" });
  const date = day === today ? "Today" : say({ weekday: "short", month: "short", day: "numeric", ...(day.slice(-4) === today.slice(-4) ? {} : { year: "numeric" }) });
  return wholeTimes(`${date}, ${time}`);
}

/**
 * A time or a date stays on one line: "6:26 PM" never breaks before PM, and
 * "Oct 20" never between the month and the day, at a narrow width. A " · "
 * between facts keeps with the fact before it, so no line starts with a
 * separator. Every sentence an area hands the kit passes through here before
 * it is drawn.
 */
function wholeTimes(text: string): string {
  return text
    .replace(/ · /g, "\u00a0· ")
    .replace(/(\d{1,2}(?::\d{2})?) ([AP]M)\b/g, "$1\u00a0$2")
    .replace(/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\.? (\d{1,2})\b/g, (match) => match.replace(" ", "\u00a0"));
}

function wholeFacts(facts: { label: string; theirs: string }[]) {
  return facts.map((fact) => ({ ...fact, theirs: wholeTimes(fact.theirs) }));
}

/** A stale read's mark owns the read time (R2); a provider's record still names its owner beside it (C4). */
function StaleOwner({ owner, readAt, className }: { owner: FactOwner; readAt: string; className: string }) {
  const { zone } = useBento();
  return (
    <span className={className}>
      {owner.system !== "imam" && `${OWNER_NAMES[owner.system]}\u00a0· `}
      <span data-stale="">As of {readTime(readAt, zone)}</span>
    </span>
  );
}

function viewOf(box: Box, open: boolean): BoxView {
  if (box.kind === "fact") return { open, draft: false };
  const kept = box.kind === "task" ? keepsDraft(box.parts) : rowsOf(box).some((row) => keepsDraft(box.row.task?.(row)?.parts));
  return { open, draft: Boolean(box.draft) || kept };
}

/** A part's browser draft differs from its record, so its box holds a draft (CASE-038, O7). */
function keepsDraft(parts: Part[] | undefined): boolean {
  return Boolean(parts?.some((part) => part.keep?.kept));
}

/** The draft is in this tab's storage, so a reload brings it back: only then do the words promise it. */
function keptInTab(parts: Part[] | undefined): boolean {
  return Boolean(parts?.some((part) => part.keep?.inTab));
}

/** The kit's words for a hidden box or row that holds a browser draft (CASE-038). */
const KEPT: BoxState = { class: "draft", word: "Draft kept in this tab" };

/**
 * A result still waiting on the person outranks the kept-draft words: pending,
 * Not confirmed, Partly done or refused keeps its own mark, and the draft is
 * what its Retry or Check works from (CASE-041 clarified, W2).
 */
function unresolved(result: Result | undefined, pending: boolean): boolean {
  return pending || (result !== undefined && result.state !== "none" && result.state !== "confirmed");
}

/** A list's rows, when its read holds them. */
function rowsOf(box: ListBoxSpec<unknown, unknown>): unknown[] {
  if (box.read.status === "ready") return box.rows(box.read.value);
  if (box.read.status === "stale") return box.rows(box.read.last);
  return [];
}

/** The open row whose task holds the list's candidate. */
/** The open row whose task holds the List's candidate, by key: a List may build its rows afresh on every call. */
/** A row task with content of its own, whose drafts stay drawn while its row is hidden (O7). */
function holdsWork(task: RowTask | undefined): boolean {
  return Boolean(task?.render || task?.parts?.length);
}

/**
 * Where each row starts. A row that opens only detail starts as it says; of
 * the rows that start open with a Task, only the first does, so one row's Task
 * is open from the first draw, and drawing, Hide, the page's row callback and
 * the lead all read that one state (CASE-041).
 */
function startsOpenOf(box: ListBoxSpec<unknown, unknown>): (row: unknown) => boolean {
  const first = rowsOf(box).find((row) => box.row.startsOpen?.(row) && box.row.task?.(row));
  const firstKey = first === undefined ? undefined : box.row.key(first);
  return (row) => Boolean(box.row.startsOpen?.(row)) && (!box.row.task?.(row) || box.row.key(row) === firstKey);
}

function candidateRow(box: ListBoxSpec<unknown, unknown>, isRowOpen: (key: string, startsOpen: boolean) => boolean): { key: string; task: RowTask } | null {
  const starts = startsOpenOf(box);
  for (const row of rowsOf(box)) {
    const task = box.row.task?.(row);
    const key = box.row.key(row);
    if (task && task.result.state !== "confirmed" && isRowOpen(key, starts(row))) return { key, task };
  }
  return null;
}

/**
 * The row whose act the List offers (CASE-032, CASE-045): drawn, with a
 * visit, a check, a leave, or an open that hasn't yet opened the row. A name
 * is page navigation and a Hide moves nothing, so neither is offered.
 */
function offeredRow(box: ListBoxSpec<unknown, unknown>, isRowOpen: (key: string, startsOpen: boolean) => boolean): { key: string; act: Exclude<RowAct, { does: "exit" }> } | null {
  if (box.offers === undefined) return null;
  const starts = startsOpenOf(box);
  const row = rowsOf(box).find((one) => box.row.key(one) === box.offers);
  const act = row === undefined ? undefined : box.row.act?.(row);
  if (!act || act.does === "exit" || (act.does === "open" && isRowOpen(box.offers, starts(row)))) return null;
  return { key: box.offers, act };
}

/**
 * The candidate a box draws (P2), or none. A resting box, a read that isn't
 * ready and a confirmed result draw no candidate. Open, the candidate is the
 * foot's commit, retry or Check, blocked by the commit's own blockers, and a
 * Fact's is its own act, which its head keeps (CASE-047).
 */
function drawnCandidate(box: Box, view: BoxView, isRowOpen: (key: string, startsOpen: boolean) => boolean): Candidate | undefined {
  const candidate = box.candidate;
  if (!candidate || restsSettled(box, view) || box.read.status !== "ready") return undefined;
  if (!view.open) return box.act ? candidate : undefined;
  if (box.kind === "task") {
    if (box.result.state === "confirmed") return undefined;
    if (box.result.state === "partial" || box.result.state === "unknown") return { ...candidate, blocked: null };
    return { ...candidate, blocked: blockersOf(box)[0] ?? null };
  }
  if (box.kind === "list") {
    const open = candidateRow(box, isRowOpen);
    // The offered row carries the server's candidate as it is: its eligibility, its block and its rank (CASE-045).
    if (!open) return offeredRow(box, isRowOpen) ? candidate : undefined;
    if (open.task.result.state === "partial" || open.task.result.state === "unknown") return { ...candidate, blocked: null };
    return { ...candidate, blocked: blockersOf(open.task)[0] ?? null };
  }
  // An open Fact draws its own act in its head, so the candidate is that act (CASE-047).
  if (box.kind === "fact") return box.act && box.act.does !== "open" ? candidate : undefined;
  return undefined;
}

export interface BentoProps {
  /** The page's scope for one lead and for geometry (`data-bento-page`). */
  id: string;
  /** The journey id, the scope for stable order across states (P4). */
  journey: string;
  /** In the job's time order; the order never depends on the clock (P1, P4). */
  boxes: Box[];
  /** Boxes that start open, such as a preset or a deep link. */
  defaultOpen?: string[];
  /** The box the person already chose. */
  defaultIntent?: string | null;
  /** Told when the person opens or hides a box. */
  onOpenChange?: (id: string, open: boolean) => void;
  /** Told when the person opens or hides a List's row, by its key, such as to keep the open row in the address. */
  onRowOpenChange?: (id: string, row: string, open: boolean) => void;
  /** An open visit. Without it, the page takes its host's slot when that slot's origin is one of its boxes. */
  visit?: BentoVisitSlot;
  /** The IANA zone the page already uses, such as its course's; a read time is said in it. The browser's own without it. */
  zone?: string;
  /**
   * A Home's two origin Lists that share a row from 1280px, 7 and 5 of 12
   * columns, in their order here, which is the order of `boxes`; below that
   * they stack. A List with a row Task open takes the row while it is open (P1,
   * CASE-048).
   */
  pair?: [string, string];
  className?: string;
}

/** The page root: it lays the boxes in order, holds intent and grants the one lead. */
export function Bento({ id, journey, boxes, defaultOpen = [], defaultIntent = null, onOpenChange, onRowOpenChange, visit: visitProp, zone, pair: pairIds, className }: BentoProps) {
  const prefix = `bento-${React.useId().replace(/[^\w-]/g, "")}`;
  const [openIds, setOpenIds] = React.useState<ReadonlySet<string>>(() => new Set(defaultOpen));
  const [rowOpen, setRowOpen] = React.useState<ReadonlyMap<string, boolean>>(() => new Map());
  const [intent, setIntent] = React.useState<string | null>(defaultIntent);
  const [inFlight, setInFlight] = React.useState<ReadonlySet<string>>(() => new Set());
  const host = React.useContext(BentoVisit);

  // A forbidden box isn't drawn at all (R1).
  const drawn = boxes.filter((box) => box.read.status !== "forbidden");
  const hostSlot = host?.slot && drawn.some((box) => box.id === host.slot?.at) ? host.slot : undefined;
  const visit = visitProp ?? hostSlot;
  // A visit open on another page of the route takes the lead from this one too: none of its boxes rank (CASE-046).
  const elsewhere = !visit && Boolean(host?.slot);
  const visited = visit ? visit.boxes.filter((box) => box.read.status !== "forbidden") : [];
  const visitIds = visit ? new Set(visited.map((box) => box.id)) : null;

  // A new visit address opens the box it names and takes intent there; other
  // open and row state stays. A named box that arrives later at the same
  // address, once the address's read resolves, opens as it arrives, and takes
  // intent only while the visit still holds it. A box already drawn stays as
  // the person left it. Closing the visit drops intent it held (O10, P3).
  const shownKey = visit ? `${visit.at}\u0000${visit.address}` : null;
  const opens = visit?.opens ?? [];
  const [shown, setShown] = React.useState<{ key: string; ids: ReadonlySet<string>; intent: string | null } | null>(null);
  const arrived = shown && visitIds && shown.key === shownKey ? [...visitIds].filter((boxId) => !shown.ids.has(boxId)) : [];
  if (arrived.length) {
    const late = opens.filter((boxId) => arrived.includes(boxId));
    const moves = late.length > 0 && intent === shown!.intent;
    setShown({ ...shown!, ids: new Set([...shown!.ids, ...arrived]), intent: moves ? late[0] : shown!.intent });
    if (late.length) setOpenIds((ids) => new Set([...ids, ...late]));
    if (moves) setIntent(late[0]);
  } else if (shownKey !== (shown?.key ?? null)) {
    if (visit && visitIds) {
      const first = opens[0] ?? visited[0]?.id ?? null;
      setShown({ key: shownKey!, ids: visitIds, intent: first });
      if (opens.length) setOpenIds((ids) => new Set([...ids, ...opens]));
      setIntent(first);
    } else {
      const was = shown?.ids;
      setShown(null);
      setIntent((current) => (current !== null && was?.has(current) ? null : current));
    }
  }
  // While a visit is open, intent outside it counts for nothing (O10, P2).
  const pageIntent = visitIds && intent !== null && !visitIds.has(intent) ? null : intent;

  const isOpen = React.useCallback((box: Box) => box.fold === "never" || openIds.has(box.id), [openIds]);
  const isRowOpen = React.useCallback(
    (box: Box, key: string, startsOpen: boolean) => rowOpen.get(`${box.id}\u0000${key}`) ?? startsOpen,
    [rowOpen],
  );
  if (import.meta.env.DEV) {
    if (visit) {
      if (!drawn.some((box) => box.id === visit.at)) throw new Error(`A visit's origin, ${visit.at}, isn't on the page.`);
      const pageIds = new Set(drawn.map((box) => box.id));
      const repeated = visited.find((box) => pageIds.has(box.id));
      if (repeated) throw new Error(`A visited box's id, ${repeated.id}, repeats a page box's id.`);
    }
    const all = [...drawn, ...visited];
    assertPage({ journey, boxes: all, intent: pageIntent !== null && all.some((box) => box.id === pageIntent) ? pageIntent : null });
    if (pairIds) {
      const at = pairIds.map((boxId) => boxes.findIndex((box) => box.id === boxId));
      if (at.some((index) => index < 0 || boxes[index].kind !== "list") || at[1] !== at[0] + 1)
        throw new Error(`${id}: a pair is two Lists next to each other in boxes, in that order: ${pairIds.join(", ")}.`);
    }
  }
  const pair = new Map<string, { with: string; span: 7 | 5 }>(pairIds ? [[pairIds[0], { with: pairIds[1], span: 7 }], [pairIds[1], { with: pairIds[0], span: 5 }]] : []);

  // Inside a visit only the boxes it brought rank (O10, P2).
  const ranked = (visit ? visited : elsewhere ? [] : drawn).map((box) => ({ ...box, candidate: drawnCandidate(box, viewOf(box, isOpen(box)), (key, starts) => isRowOpen(box, key, starts)) }) as Box);
  const lead = leadOf({ boxes: ranked, intent: pageIntent });
  // A box outside the visit that starts its own work is the host's to answer: it replaces the visit (O10, O7),
  // from another page on the route too (CASE-046).
  const outside = (boxId: string) => {
    if (visit && visitIds && !visitIds.has(boxId)) visit.onOutside?.(boxId);
    else if (elsewhere) host!.slot!.onOutside?.(boxId);
  };

  const value: BentoContextValue = {
    zone,
    pair,
    prefix,
    lead,
    intent: pageIntent,
    isOpen,
    isRowOpen,
    visit: visit && visitIds ? { at: visit.at, ids: visitIds } : elsewhere ? { at: host!.slot!.at, ids: new Set() } : null,
    open(boxId) {
      setOpenIds((ids) => new Set(ids).add(boxId));
      setIntent(boxId);
      onOpenChange?.(boxId, true);
      outside(boxId);
    },
    hide(boxId) {
      setOpenIds((ids) => {
        const next = new Set(ids);
        next.delete(boxId);
        return next;
      });
      setIntent((current) => (current === boxId ? null : current));
      onOpenChange?.(boxId, false);
    },
    complete(boxId) {
      setIntent((current) => (current === boxId ? null : current));
    },
    toggleRow(boxId, key, open) {
      setRowOpen((map) => new Map(map).set(`${boxId}\u0000${key}`, open));
      // A row the person opens is their choice of the List, as opening a box is (P3).
      setIntent((current) => (open ? boxId : current === boxId ? null : current));
      onRowOpenChange?.(boxId, key, open);
      if (open) outside(boxId);
    },
    intend(boxId) {
      // Every keystroke calls this; only a change of box is a new state.
      if (intent !== boxId) setIntent(boxId);
      outside(boxId);
    },
    edit: outside,
    isPending: (key) => inFlight.has(key),
    setPending(key, pending) {
      setInFlight((keys) => {
        const next = new Set(keys);
        if (pending) next.add(key);
        else next.delete(key);
        return next;
      });
    },
  };

  // The visit sits right after its origin, a sibling in the same list, so no
  // page box remounts when it opens or closes (O10, P4).
  const children: React.ReactNode[] = [];
  for (const box of drawn) {
    children.push(drawBox(box));
    if (visit && box.id === visit.at)
      children.push(<React.Fragment key={"\u0000visit"}>{visit.render(<div className="bento">{visited.map(drawBox)}</div>)}</React.Fragment>);
  }

  // In development, an id drawn twice on one page is named, since aria-controls, focus and the judge would each find the wrong element.
  const pageRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!import.meta.env.DEV || !pageRef.current) return;
    const ids = Array.from(pageRef.current.querySelectorAll("[id]"), (element) => element.id);
    const repeated = ids.find((one, index) => ids.indexOf(one) !== index);
    if (repeated) console.warn(`${id}: the id ${repeated} is drawn twice on this page.`);
  });

  return (
    <BentoContext.Provider value={value}>
      <div ref={pageRef} data-bento-page={id} data-journey={journey} className={cn("bento", className)}>
        {children}
      </div>
    </BentoContext.Provider>
  );
}

function drawBox(box: Box) {
  if (box.kind === "fact") return <FactBox key={box.id} box={box} />;
  if (box.kind === "list") return <ListBox key={box.id} box={box} />;
  return <TaskBox key={box.id} box={box} />;
}

/** A page the person may not see, or whose read can't resolve: one sentence and a way back (R1, CASE-018). */
export function PageRefusal({ id, journey, refusal }: { id: string; journey: string; refusal: PageRefusalSpec }) {
  const sentence = React.useRef<HTMLParagraphElement>(null);
  // The refusal is the page, so its sentence takes focus on arrival (CASE-022).
  React.useEffect(() => sentence.current?.focus(), []);
  return (
    <div data-bento-page={id} data-journey={journey} data-page-refusal={refusal.reason} className="bento-refusal">
      <p ref={sentence} tabIndex={-1}>
        {wholeTimes(refusal.sentence)}
      </p>
      {/* A Home has no way back: an unavailable one reads again (CASE-032). */}
      {refusal.back ? (
        <Link data-act="exit" to={refusal.back.to} className={QUIET_LINK}>
          {refusal.back.label}
        </Link>
      ) : (
        refusal.retry && <ActButton act="check" onPress={refusal.retry}>Try again</ActButton>
      )}
    </div>
  );
}

/**
 * A name that links to its page. It wraps as text, so its padding, not a size,
 * makes it a target of 24px, 44px for a finger, even a name of one letter. A
 * margin takes back the padding's width, so no word or line moves.
 */
const NAME_LINK =
  "-mx-3 px-3 py-1 pointer-coarse:-mx-[calc(var(--control-height-compact)/2)] pointer-coarse:px-[calc(var(--control-height-compact)/2)] pointer-coarse:py-3 hover:underline";

/** A box's label, or a link to the page it names: page navigation, never an act (CASE-032). */
function BoxLabel({ box }: { box: { label: string; labelTo?: string } }) {
  if (!box.labelTo) return <>{box.label}</>;
  return (
    <Link data-nav="" to={box.labelTo} className={NAME_LINK}>
      {box.label}
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Marks and acts                                                      */
/* ------------------------------------------------------------------ */

function Shape({ state }: { state: StateClass }) {
  return <i aria-hidden className="bento-g" data-shape={state} />;
}

/** A state: its shape and its word, never a chip (F2). */
export function StateMark({ state, className }: { state: BoxState; className?: string }) {
  return (
    <span data-state={state.class} className={cn("bento-mark", className)}>
      <Shape state={state.class} />
      {wholeTimes(state.word)}
    </span>
  );
}

/** The hooks a candidate act carries (P2). */
function candidateHooks(candidate: Candidate | undefined, lead: boolean) {
  if (!candidate) return {};
  return {
    "data-candidate": "",
    "data-eligible": String(candidate.eligible),
    "data-rank": candidate.rank ?? undefined,
    "data-lead": lead ? "" : undefined,
  };
}

/** A quiet act, or the page's one filled act. */
function ActButton({
  act,
  lead = false,
  candidate,
  blockedBy,
  pending = false,
  onPress,
  visitTo,
  className,
  children,
}: {
  act: "open" | "check" | "commit" | "exit" | "visit" | "leave";
  lead?: boolean;
  candidate?: Candidate;
  /** The ids of the visible reasons a blocked act is described by (F5). */
  blockedBy?: string[];
  pending?: boolean;
  onPress?: () => void;
  /** A visit act's address, which the host reads to list only the fallbacks reachable from the page (O10). */
  visitTo?: VisitAddress;
  className?: string;
  children: React.ReactNode;
}) {
  const blocked = blockedBy !== undefined;
  // An act with nothing to press, such as a visit with no host, is drawn quiet and disabled.
  const filled = lead && !blocked && onPress !== undefined;
  return (
    <Button
      type="button"
      variant={filled ? "default" : "subtle"}
      className={cn(!filled && "bg-primary-wash", blocked && !pending && "cursor-not-allowed opacity-50", className)}
      data-act={act}
      data-visit-to={visitTo}
      {...candidateHooks(candidate, filled)}
      data-blocked={blocked ? "" : undefined}
      aria-describedby={blocked ? blockedBy.join(" ") : undefined}
      // A blocked act, or a save in flight, stays focusable and hit-testable, so
      // its reason is reachable and focus never drops; pressing it does nothing (F5, O4, P5).
      aria-disabled={blocked || pending || onPress === undefined || undefined}
      onClick={() => {
        if (!blocked && !pending) onPress?.();
      }}
    >
      {children}
    </Button>
  );
}

/**
 * A quiet act or link, wherever it's drawn: a target of at least 24px for a
 * mouse (WCAG 2.5.8) and the kit's compact control height, 44px, for a finger.
 */
const QUIET_LINK =
  "inline-flex min-h-6 min-w-6 items-center pointer-coarse:min-h-[var(--control-height-compact)] pointer-coarse:min-w-[var(--control-height-compact)] text-ui font-semibold text-primary hover:underline";

function LinkAct({
  act = "exit",
  expanded,
  controls,
  onPress,
  visitTo,
  children,
}: {
  act?: "exit" | "check" | "open" | "visit" | "leave";
  /** A Hide or Show: whether what it discloses is shown. */
  expanded?: boolean;
  /** The id of what a Hide or Show discloses, inside its box (CASE-040). */
  controls?: string;
  /** Without it the act is drawn disabled and does nothing. */
  onPress?: () => void;
  /** A visit act's address (O10). */
  visitTo?: VisitAddress;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-act={act}
      data-visit-to={visitTo}
      aria-expanded={expanded}
      aria-controls={controls}
      aria-disabled={onPress === undefined || undefined}
      className={QUIET_LINK}
      onClick={onPress}
    >
      {children}
    </button>
  );
}

/** A quiet visit: the host shows the addressed box in this box's place (O10). */
function VisitLink({ address, box, next, children }: { address: VisitAddress; box: string; next?: true; children: React.ReactNode }) {
  const press = useVisitPress(address, box, next);
  return (
    <LinkAct act="visit" visitTo={address} onPress={press ?? undefined}>
      {children}
    </LinkAct>
  );
}

/** A face act that visits (O10, F4). It can be its box's candidate and lead. */
function VisitAct({ address, box, next, lead, candidate, children }: { address: VisitAddress; box: string; next?: true; lead: boolean; candidate: Candidate | undefined; children: React.ReactNode }) {
  const press = useVisitPress(address, box, next);
  return (
    <ActButton act="visit" visitTo={address} lead={lead} candidate={candidate} onPress={press ?? undefined}>
      {children}
    </ActButton>
  );
}

/** A link to another page, or to a provider's page in a new tab (`leave`). */
function ExitLink({ exit, box, onReopen, kind = "exit" }: { exit: Exit; box: string; onReopen: () => void; kind?: "exit" | "leave" }) {
  if (exit.visit)
    return (
      <VisitLink address={exit.visit} box={box} next={exit.next}>
        {exit.label}
      </VisitLink>
    );
  // Reopening the box in place opens it, as the face's own open act does; it never commits (F4).
  if (exit.reopens || !exit.to) return <LinkAct act="open" onPress={onReopen}>{exit.label}</LinkAct>;
  if (exit.external || kind === "leave")
    return (
      <a data-act={kind} href={exit.to} target="_blank" rel="noopener noreferrer" className={QUIET_LINK}>
        {exit.label}
        <span className="sr-only"> (opens in a new tab)</span>
      </a>
    );
  // Another IMAM page is page navigation, never an exit act (CASE-032, CASE-040).
  return (
    <Link data-nav="" to={exit.to} className={QUIET_LINK}>
      {exit.label}
    </Link>
  );
}

/** An exit or a leave that is its box's candidate (CASE-023): drawn as an act, so it can lead. */
function ExitAct({ kind, verb, to, external, lead, candidate }: { kind: "exit" | "leave"; verb: string; to: string; external?: boolean; lead: boolean; candidate: Candidate }) {
  const hooks = { "data-act": kind, ...candidateHooks(candidate, lead) };
  return (
    <Button asChild variant={lead ? "default" : "subtle"} className={cn(!lead && "bg-primary-wash")}>
      {external || kind === "leave" ? (
        <a {...hooks} href={to} target="_blank" rel="noopener noreferrer">
          {verb}
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      ) : (
        <Link {...hooks} to={to}>
          {verb}
        </Link>
      )}
    </Button>
  );
}

/**
 * A leave whose provider window the area's adapter opens (F4). It is called
 * within the press, so the window isn't blocked, and it is held until it
 * settles, so a second press opens no second window. Nothing it resolves
 * passes through the kit.
 */
function LeaveRun({ verb, run, lead, candidate }: { verb: string; run: () => Promise<void>; lead: boolean; candidate: Candidate | undefined }) {
  const [pending, setPending] = React.useState(false);
  const press = () => {
    setPending(true);
    void (async () => {
      try {
        await run();
      } finally {
        setPending(false);
      }
    })();
  };
  const label = (
    <>
      {verb}
      <span className="sr-only"> (opens a new window)</span>
    </>
  );
  if (!candidate) return <LinkAct act="leave" onPress={pending ? undefined : press}>{label}</LinkAct>;
  return (
    <ActButton act="leave" lead={lead} candidate={candidate} pending={pending} onPress={press}>
      {label}
    </ActButton>
  );
}

function newRequestId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

/* ------------------------------------------------------------------ */
/* The frame every kind shares                                         */
/* ------------------------------------------------------------------ */

interface Frame {
  id: string;
  open: boolean;
  view: BoxView;
  lead: boolean;
  candidate: Candidate | undefined;
  /** What blocks the box's act, whether or not it ranks. */
  blocked: Blocker | null;
  /**
   * A DOM id for one kind of the kit's elements, from the values that name it
   * (CASE-024 targets, O10 focus). Each value is encoded so it never holds the
   * separator, so an area's part id "row-5" can't meet row 5's region.
   */
  domId: (kind: DomKind, ...values: string[]) => string;
  focus: (target: FocusTarget) => void;
  openBox: (target?: FocusTarget) => void;
  hideBox: () => void;
  /**
   * The person changed a value here (O8, P3). Bind it to bubbling handlers only:
   * in a capture handler its render lands, in a browser, before a controlled
   * field's own onChange, and the field loses that edit.
   */
  intend: () => void;
  /** A Fact's field changed; bubbling handlers only, as `intend`. */
  edit: () => void;
  rootRef: React.RefObject<HTMLElement | null>;
}

/** The kinds of element the kit names: one prefix each, so no two kinds share an id. */
type DomKind = "foot" | "box" | "heading" | "blocked" | "part" | "row" | "title" | "col" | "cell" | "field" | "blocker" | "end";

/** An id token: letters and digits as they are, every other character as _ and four hex digits, so "-" never appears in it. */
function idToken(value: string): string {
  return value.replace(/[^A-Za-z0-9]/g, (char) => `_${char.charCodeAt(0).toString(16).padStart(4, "0")}`);
}

/** Moves focus after a press once the target is drawn, so focus never drops (P5). */
function useFrame(box: Box): Frame {
  const bento = useBento();
  const open = bento.isOpen(box);
  const view = viewOf(box, open);
  const rootRef = React.useRef<HTMLElement | null>(null);
  const [target, setTarget] = React.useState<FocusTarget | null>(null);
  const domId = React.useCallback(
    (kind: DomKind, ...values: string[]) => [bento.prefix, idToken(box.id), kind, ...values.map(idToken)].join("-"),
    [bento.prefix, box.id],
  );

  // No dependency list: a target drawn by a later render, such as a result the
  // consumer sets after its commit resolves, is focused on that render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useLayoutEffect(() => {
    const root = rootRef.current;
    if (!target || !root) return;
    const selectors =
      target === "face"
        ? ['[data-face] [data-act]:not(:disabled):not([aria-disabled="true"])', "[data-face]"]
        : target === "head"
          ? ["[data-face]"]
          : target === "result"
            ? [".bento-refused", "[data-result]"]
            : "part" in target
              ? [`[id="${domId("part", target.part)}-heading"]`]
              : [`[id="${domId("row", target.row)}"]`];
    const docked = target === "result" ? document.getElementById(domId("foot", "foot")) : null;
    const element = selectors.map((selector) => root.querySelector<HTMLElement>(selector)).find(Boolean)
      ?? docked?.querySelector<HTMLElement>(".bento-refused") ?? docked;
    if (!element) return;
    element.focus();
    if (typeof target !== "string") element.scrollIntoView?.({ block: "nearest" });
    setTarget(null);
  });

  const drawn = drawnCandidate(box, view, (key, starts) => bento.isRowOpen(box, key, starts));
  // While a visit is open, a box outside it draws no candidate (O10, P2).
  const ranks = bento.visit === null || bento.visit.ids.has(box.id);
  return {
    id: box.id,
    open,
    view,
    lead: bento.lead === box.id,
    candidate: ranks ? drawn : undefined,
    blocked: drawn?.blocked ?? null,
    domId,
    focus: setTarget,
    openBox(focusTarget = "head") {
      bento.open(box.id);
      setTarget(focusTarget);
    },
    hideBox() {
      bento.hide(box.id);
      setTarget("face");
    },
    intend: () => bento.intend(box.id),
    edit: () => bento.edit(box.id),
    rootRef,
  };
}

function BoxRoot({ box, frame, rowTask = false, children }: { box: Box; frame: Frame; rowTask?: boolean; children: React.ReactNode }) {
  const bento = useBento();
  const rest = restsSettled(box, frame.view);
  const tone = box.kind === "fact" && box.tone ? box.tone : null;
  // A visit's origin stays this same node, hidden and inert, until Return (O10).
  const origin = bento.visit?.at === box.id;
  const list = box.kind === "list" ? box : null;
  const pair = bento.pair.get(box.id);
  return (
    <DetailCard
      as="section"
      transparent={box.presentation === "embedded" || (box.kind === "list" && box.working)}
      data-presentation={box.presentation ?? (box.kind === "list" && box.working ? "working" : undefined)}
      ref={frame.rootRef}
      id={frame.domId("box")}
      hidden={origin || undefined}
      inert={origin || undefined}
      style={tone ? ({ "--tile-hue": tone.identity } as React.CSSProperties) : undefined}
      aria-label={box.label}
      // Covers are their own surfaces, so their List draws no frame around them (CASE-048).
      className={cn("bento-box p-0 gap-0", (box.presentation === "embedded" || (box.kind === "list" && box.working)) && "flex min-w-0 flex-col", box.presentation === "embedded" && "[&>.bento-part]:px-0 [&>.bento-fields]:px-0", tone && "border-0 bg-[color-mix(in_oklab,var(--tile-hue)_13%,var(--card))]", list?.layout === "covers" && "bento-frameless")}
      data-box={box.kind}
      data-box-id={box.id}
      data-home={list?.home ? "" : undefined}
      data-layout={list?.layout}
      // The declared pair, always, and whether this List has a row Task open, which takes it the row (P1, CASE-048).
      data-pair={pair?.with}
      data-pair-span={pair?.span}
      data-row-task-open={rowTask ? "" : undefined}
      // The row whose act this List offers to the ranking, as the server names it (CASE-045).
      data-offers={box.kind === "list" ? box.offers : undefined}
      data-read={box.read.status}
      data-draft={box.kind === "fact" ? undefined : String(frame.view.draft)}
      data-rest={rest ? "" : undefined}
      data-open={frame.open ? "" : undefined}
      data-intent={bento.intent === box.id ? "" : undefined}
      data-w={box.width === 2 ? "2" : undefined}
    >
      {children}
    </DetailCard>
  );
}

/* ------------------------------------------------------------------ */
/* The face                                                            */
/* ------------------------------------------------------------------ */

function Figures({ figures }: { figures: Figure[] }) {
  return (
    <div className="bento-figs">
      {figures.map((figure) => (
        <div key={figure.unit} className="bento-fig">
          <b data-figure="">{figure.value}</b>
          <span>{figure.unit}</span>
        </div>
      ))}
    </div>
  );
}

/** The settled face, one line. A folded confirmed result is this face, and it takes focus (F3, W2). */
function RestFace({ box, frame, state }: { box: Box; frame: Frame; state: BoxState }) {
  const value = box.read.status === "ready" ? box.read.value : undefined;
  const folded = foldedResult(box, frame.view);
  // At rest too, a provider's record and an opted-in IMAM record say whose it is and when it was read, after the facts (C4, C5, CASE-039).
  const owner = ownerLine(box, useBento().zone);
  return (
    <div data-face="" data-box-head="" data-rest-face="" className="bento-face" tabIndex={-1} data-result={folded ? "confirmed" : undefined} role={folded ? "status" : undefined}>
      <p className="bento-rest-line">
        <span className="bento-name"><BoxLabel box={box} /></span>
        <StateMark state={state} />
        <span className="bento-facts">{value === undefined ? "" : wholeTimes(box.detail(value))}</span>
        {/* Where it came from follows what the mark says, as at the end of a closed face's label line (F3). */}
        {owner && <span className="bento-own">{owner}</span>}
      </p>
      {box.exits && (
        <span className="bento-exits">
          {box.exits.map((exit) => (
            <ExitLink key={exit.label} exit={exit} box={box.id} onReopen={() => frame.openBox()} />
          ))}
        </span>
      )}
    </div>
  );
}

/** A blocked act stays where it is, quiet and disabled, described by its reason (F5). */
function BlockedAct({ verb, frame }: { verb: string; frame: Frame }) {
  return (
    <span className="bento-blocked">
      <ActButton act="open" candidate={frame.candidate} blockedBy={[frame.domId("blocked")]}>
        {verb}
      </ActButton>
    </span>
  );
}

/**
 * A blocked act's reason, right under the head and linked to the part that
 * fixes it (F5). Its link belongs to the reason, so it sits outside the head's
 * one act (F1).
 */
function BlockedReason({ blocker, frame, parts }: { blocker: Blocker; frame: Frame; parts: Part[] }) {
  const part = parts.find((p) => p.id === blocker.part);
  return (
    <p id={frame.domId("blocked")} className="bento-why bento-face-why">
      <Shape state="waiting" />
      <span>
        {wholeTimes(blockerSentence(blocker))}{" "}
        {blocker.kind === "missing" && blocker.field
          ? <LinkAct onPress={() => { frame.openBox({ part: blocker.part }); blocker.field!.focus(); }}>Go to {blocker.field.label.toLowerCase()}</LinkAct>
          : part && <LinkAct onPress={() => frame.openBox({ part: part.id })}>Go to {part.label.toLowerCase()}</LinkAct>}
      </span>
    </p>
  );
}

/** The face: the label, the state as the headline, one detail line and at most one act (F1). */
function Face({ box, frame }: { box: Box; frame: Frame }) {
  const bento = useBento();
  const { zone } = bento;
  const read = box.read;
  // Hidden with a browser draft, a box says so in the kit's words, once its read is there and no send is owed an answer.
  const kept = box.kind === "task" && read.status === "ready" && keptInTab(box.parts) && !unresolved(box.result, bento.isPending(`${frame.id}\u0000foot`));
  const state = kept ? KEPT : stateOf(box);
  if (state && restsSettled(box, frame.view)) return <RestFace box={box} frame={frame} state={state} />;

  const value = read.status === "ready" ? read.value : read.status === "stale" ? read.last : undefined;
  const owner = ownerLine(box, zone);
  const parts = box.kind === "task" ? box.parts : [];

  let headline: React.ReactNode;
  if (read.status === "ready" && box.kind === "fact" && box.figures) headline = <Figures figures={box.figures(read.value)} />;
  else if (read.status === "ready" && box.kind === "fact" && box.prose) headline = <p className="bento-prose">{wholeTimes(box.prose(read.value))}</p>;
  else if (read.status === "empty" && box.kind === "fact") headline = <p className="bento-prose">{wholeTimes(read.sentence)}</p>;
  else if (read.status === "stale" && state) headline = <StateMark state={{ class: "reading", word: state.word }} />;
  else if (state) headline = <StateMark state={state} />;

  let detail: React.ReactNode = null;
  let sub: React.ReactNode = null;
  if (read.status === "loading") detail = <span className="bento-skel" style={{ width: "80%" }} />;
  else if (read.status === "failed") detail = wholeTimes(read.detail ?? "");
  else if (read.status === "empty" || read.status === "unread") sub = read.sub ? <span className="bento-sub">{wholeTimes(read.sub)}</span> : null;
  else if (value !== undefined) {
    detail = wholeTimes(box.detail(value));
    if (read.status === "stale") sub = <span className="bento-sub" data-stale="">{wholeTimes(read.sentence)}</span>;
    else if (box.sub) sub = <span className="bento-sub">{wholeTimes(box.sub(value))}</span>;
  }

  let act: React.ReactNode = null;
  let blocker: Blocker | null = null;
  if (read.status === "failed")
    act = <ActButton act="check" onPress={() => { read.retry(); frame.focus("face"); }}>Try again</ActButton>;
  else if (read.status === "stale")
    act = <ActButton act="check" onPress={() => { read.refresh(); frame.focus("face"); }}>Refresh</ActButton>;
  else ({ act, blocker } = ownAct(box, frame, "face"));
  const exits = read.status === "ready" && box.exits ? box.exits : [];
  if (box.presentation === "embedded" && read.status === "ready") return (
    <div data-face="" data-box-head="" tabIndex={-1} className="space-y-2 outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <DetailCardHead title={<BoxLabel box={box} />} counter={state && <StateMark state={state} />} />
      <div className="text-ui text-foreground-soft">{detail}{sub}</div>
      {(act || exits.length > 0) && <div className="flex flex-wrap items-center justify-between gap-3">{act}<span className="bento-exits">{exits.map((exit) => <ExitLink key={exit.label} exit={exit} box={box.id} onReopen={() => frame.openBox()} />)}</span></div>}
      {blocker && <BlockedReason blocker={blocker} frame={frame} parts={parts} />}
    </div>
  );

  return (
    <>
      <div data-face="" data-box-head="" className="bento-face" tabIndex={-1}>
        <p className="bento-label">
          <span><BoxLabel box={box} /></span>
          {read.status === "stale" ? (
            <StaleOwner owner={box.owner} readAt={read.readAt} className="bento-own" />
          ) : (
            owner && <span className="bento-own">{owner}</span>
          )}
        </p>
        <div className="bento-headline">{headline}</div>
        <p className="bento-detail">
          {detail}
          {sub}
        </p>
        {(act || exits.length > 0) && (
          <div className="bento-act-row">
            {act ?? <span />}
            {exits.length > 0 && (
              <span className="bento-exits">
                {exits.map((exit) => (
                  <ExitLink key={exit.label} exit={exit} box={box.id} onReopen={() => frame.openBox()} />
                ))}
              </span>
            )}
          </div>
        )}
      </div>
      {blocker && <BlockedReason blocker={blocker} frame={frame} parts={parts} />}
    </>
  );
}

/**
 * A box's own act, with its lead and candidate (F4, P2), or blocked and
 * described by its reason (F5). A ready read draws it, and an empty or unread
 * one when it reads again or leaves for the provider, its way out (R1, C5).
 * A face draws it, and so does an open Fact's head (CASE-047).
 */
function ownAct(box: Box, frame: Frame, at: "face" | "head"): { act: React.ReactNode; blocker: Blocker | null } {
  const read = box.read;
  const acts = read.status === "ready" || ((read.status === "empty" || read.status === "unread") && wayOutOf(box) !== undefined);
  if (!acts || !box.act) return { act: null, blocker: null };
  const faceAct = box.act;
  if (frame.blocked) return { act: <BlockedAct verb={faceAct.verb} frame={frame} />, blocker: frame.blocked };
  if (faceAct.does === "visit")
    return {
      act: (
        <VisitAct address={faceAct.visit} box={box.id} next={faceAct.next} lead={frame.lead} candidate={frame.candidate}>
          {faceAct.verb}
        </VisitAct>
      ),
      blocker: null,
    };
  if (faceAct.does === "leave" && faceAct.run) return { act: <LeaveRun verb={faceAct.verb} run={faceAct.run} lead={frame.lead} candidate={frame.candidate} />, blocker: null };
  if ((faceAct.does === "exit" && faceAct.to) || faceAct.does === "leave") {
    const kind = faceAct.does;
    const to = faceAct.does === "leave" ? faceAct.providerUrl : faceAct.to!;
    const external = faceAct.does === "exit" ? faceAct.external : true;
    return {
      act: frame.candidate ? (
        <ExitAct kind={kind} verb={faceAct.verb} to={to} external={external} lead={frame.lead} candidate={frame.candidate} />
      ) : (
        <ExitLink kind={kind} exit={{ label: faceAct.verb, to, external }} box={box.id} onReopen={() => frame.openBox()} />
      ),
      blocker: null,
    };
  }
  return {
    act: (
      <ActButton
        act={faceAct.does}
        lead={frame.lead}
        candidate={frame.candidate}
        onPress={() => {
          if (faceAct.does === "open") frame.openBox(faceAct.opens ? { part: faceAct.opens } : "head");
          else if (box.kind === "task" && box.result.state === "unknown") void runAndFocus(box.result.check.run, frame, box.parts);
          else {
            if (faceAct.does === "check") faceAct.run?.();
            frame.focus(at);
          }
        }}
      >
        {faceAct.verb}
      </ActButton>
    ),
    blocker: null,
  };
}

/** The face's Check answers the send already out, so a confirmation ends the drafts it carried (CASE-038). */
async function runAndFocus(run: () => Promise<Result>, frame: Frame, parts: Part[]) {
  const sent = parts.map((part) => part.keep?.sending(true));
  const next = await run();
  if (next.state === "confirmed") for (const end of sent) end?.(next.version);
  frame.focus("result");
}

/**
 * An open box's head: its label, its one state and Hide (O1). The closed
 * detail gives way to the parts; only `openDetail`, a fact no part or effect
 * states, draws a line here (CASE-025).
 */
function Head({ box, frame, act, children }: { box: Box; frame: Frame; act?: React.ReactNode; children?: React.ReactNode }) {
  const line = React.useRef<HTMLDivElement>(null);
  const crowded = useCrowded(line);
  const read = box.read;
  // Open, a Task may say what its head means while a part carries the resting word (CASE-030).
  const state = openStateOf(box);
  const value = read.status === "ready" ? read.value : read.status === "stale" ? read.last : undefined;
  const said = value === undefined ? (read.status === "failed" ? read.detail : undefined) : box.openDetail?.(value as never);
  const detail = said && wholeTimes(said);
  const owner = ownerLine(box, useBento().zone);
  // Hide folds the box, the one thing that stays when it does, so the box is what it discloses (CASE-040).
  const hide = box.fold === "never" ? undefined : <LinkAct expanded controls={frame.domId("box")} onPress={frame.hideBox}>Hide</LinkAct>;
  // Open, a box still offers its ways elsewhere, such as a List's "Add section"; an exit that only reopens it has nothing to do here.
  const ways = (box.exits ?? []).filter((exit) => !exit.reopens && Boolean(exit.to || exit.visit));
  const wayLinks = ways.map((exit) => <ExitLink key={exit.label} exit={exit} box={box.id} onReopen={() => undefined} />);
  // A Home's List says its head on one line: the label, a ready count bare, and its exit at the end (F1, CASE-048).
  const home = box.kind === "list" && box.home === true;
  const covers = box.kind === "list" && box.layout === "covers";
  let mark: React.ReactNode = state && <StateMark state={read.status === "stale" ? { class: "reading", word: state.word } : state} />;
  // Covers are counted at a glance, so a ready List of them draws no count.
  if (read.status === "ready" && covers) mark = null;
  else if (read.status === "ready" && home && state) {
    if (import.meta.env.DEV && (state.class !== "quiet" || !/^\d+\+?$/.test(state.word)))
      throw new Error(`${box.id}: a Home List's ready state is quiet and its word is the bare count, such as "3" or "50+", not "${state.word}".`);
    mark = (
      <span data-state="quiet" data-count="" className="bento-count">
        {state.word}
      </span>
    );
  }
  if (box.presentation === "embedded" && (read.status === "ready" || read.status === "stale")) return (
    <div data-face="" data-box-head="" tabIndex={-1} className="space-y-2 outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <div className="flex items-center justify-between gap-3">
        <DetailCardHead title={<BoxLabel box={box} />} counter={mark} />
        {hide}
      </div>
      {children}
      {detail && <p className="text-ui text-foreground-soft">{detail}</p>}
      {read.status === "stale" && <p className="text-ui text-status-warning-foreground">{read.sentence} <LinkAct act="check" onPress={() => { read.refresh(); frame.focus("head"); }}>Refresh</LinkAct></p>}
      {(act || wayLinks.length > 0) && <div className="flex flex-wrap items-center justify-between gap-3">{act}<span className="bento-exits">{wayLinks}</span></div>}
    </div>
  );
  return (
    <div data-face="" data-box-head="" className="bento-head" tabIndex={-1}>
      <div ref={line} className="bento-head-line" data-crowded={crowded ? "" : undefined}>
        <TileHeader
          id={frame.domId("heading")}
          title={<BoxLabel box={box} />}
          count={
            <>
              {mark}
              {/* As on the face, the stale mark owns the read time (R2). */}
              {read.status === "stale" ? (
                <StaleOwner owner={box.owner} readAt={read.readAt} className="ms-3" />
              ) : (
                owner && <span className="ms-3">{owner}</span>
              )}
            </>
          }
          action={home && wayLinks.length > 0 ? <span className="bento-exits">{wayLinks}{hide}</span> : hide}
        />
      </div>
      {children}
      {(detail || read.status === "stale") && (
        <p className="bento-detail">
          {detail}
          {read.status === "stale" && (
            <span className="bento-sub" data-stale="">
              {wholeTimes(read.sentence)}{" "}
              <LinkAct act="check" onPress={() => { read.refresh(); frame.focus("head"); }}>Refresh</LinkAct>
            </span>
          )}
        </p>
      )}
      {/* An open Fact's own act sits where its face draws it, beside its ways (CASE-047). */}
      {act ? (
        <div className="bento-act-row">
          {act}
          {ways.length > 0 && !home && <span className="bento-exits">{wayLinks}</span>}
        </div>
      ) : (
        ways.length > 0 && !home && <p className="bento-exits bento-head-ways">{wayLinks}</p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Parts and the foot                                                  */
/* ------------------------------------------------------------------ */

/**
 * A part: its label, its own state and its content (O2). Content the person
 * has seen stays drawn, hidden, while the part or its box is hidden, unless
 * the part holds no draft, so a control's own draft survives Hide (O7).
 * While its box is hidden, the part draws only that kept content.
 */
function PartSection({ part, frame, level, hidden = false }: { part: Part; frame: Frame; level: "h3" | "h4"; hidden?: boolean }) {
  const [folded, setFolded] = React.useState(() => part.fold !== "never" && (part.state.class === "done" || part.state.class === "quiet"));
  const isFolded = folded && !part.conflict;
  const [seen, setSeen] = React.useState(!isFolded);
  if (!isFolded && !seen) setSeen(true);
  const Heading = level;
  const owner = ownerName(part.owner, useBento().zone);
  const id = frame.domId("part", part.id);
  const choose = (run: () => void) => {
    run();
    frame.focus({ part: part.id });
  };
  const region = Boolean(part.conflict || part.render);
  const content = region && (!isFolded || (seen && part.draft !== "none"));
  return (
    <section
      className="bento-part"
      id={id}
      hidden={hidden}
      aria-labelledby={`${id}-heading`}
      data-conflict={String(Boolean(part.conflict))}
      onChange={frame.intend}
      onInput={frame.intend}
    >
      {!hidden && (
        <header>
          <Heading id={`${id}-heading`} tabIndex={-1}>
            {part.label}
          </Heading>
          <span className="bento-part-end">
            {owner && <span className="text-meta text-muted-foreground">{owner}</span>}
            {!part.markedByBox && <StateMark state={part.state} />}
            {/* Show or Hide only where there is content to disclose: a summary is always shown (CASE-040). */}
            {part.fold !== "never" && !part.conflict && region && (
              <LinkAct expanded={!isFolded} controls={`${id}-content`} onPress={() => setFolded((was) => !was)}>
                {isFolded ? "Show" : "Hide"}
              </LinkAct>
            )}
          </span>
        </header>
      )}
      {!hidden && part.summary && <p className="bento-part-summary">{wholeTimes(part.summary)}</p>}
      {/* Drawn whenever the part has content, so its Show or Hide always names what it discloses (CASE-040). */}
      {region && (
        <div id={`${id}-content`} className="bento-part-content" hidden={isFolded} onClick={frame.intend}>
          {content && part.conflict && (
            <ConflictNotice
              object={part.label.toLowerCase()}
              message={wholeTimes(part.conflict.sentence)}
              facts={wholeFacts(part.conflict.facts)}
              keepMineLabel={part.conflict.keepMine.label}
              loadTheirsLabel={part.conflict.loadNewer.label}
              onKeepMine={() => choose(part.conflict!.keepMine.run)}
              onLoadTheirs={() => choose(part.conflict!.loadNewer.run)}
            />
          )}
          {content && part.render?.()}
        </div>
      )}
    </section>
  );
}

/**
 * The foot is the review (O3): what the commit will do, the named choices,
 * the blockers and one commit. After the commit it holds the result in one of
 * four shapes (W1), and the result takes focus (W2).
 */
function Foot({
  commit,
  result,
  parts = [],
  frame,
  candidate,
  lead,
  onConfirmed,
  className,
  scope,
  over,
}: {
  commit: Commit;
  result: Result;
  parts?: Part[];
  frame: Frame;
  candidate: Candidate | undefined;
  lead: boolean;
  onConfirmed: () => void;
  className?: string;
  /** A row's own task keeps its blocker ids apart from the box's. */
  scope: string;
  /** A bulk Task's selection, named above its effects (R6). */
  over?: BulkScope;
}) {
  const bento = useBento();
  // The page holds the commit in flight, so a hidden and reopened foot still
  // blocks it until its result is known (O8, W1).
  const key = `${frame.id}\u0000${scope}`;
  const pending = bento.isPending(key);
  // The result as drawn, without its functions: a new one is one that says something else.
  const shown = JSON.stringify(result);
  // What the foot showed when its commit went out, until a result takes focus.
  const owed = React.useRef<string | null>(pending ? shown : null);
  // `again` is a Check or Retry of the send already out: its answer is for what that send carried.
  const press = async (run: () => Promise<Result>, again = false) => {
    owed.current = shown;
    bento.setPending(key, true);
    // Bound now, to the drafts this commit sends: its answer ends those, never a later record's or later text (CASE-038, W1).
    const sent = (parts ?? []).map((part) => part.keep?.sending(again));
    try {
      const next = await run();
      if (next.state === "confirmed") {
        for (const end of sent) end?.(next.version);
        onConfirmed();
      }
    } finally {
      bento.setPending(key, false);
    }
  };

  // When the commit settles, its result takes focus in this foot, never
  // another row's, whichever foot pressed it (W2, P5). An area may draw its
  // result before its run resolves, so the result takes focus as it is drawn,
  // or else when the commit settles. An area clearing its last result draws
  // none: the pressed act keeps focus, and any result after it is new. The
  // foot's root is one element across every result, so it is there when the
  // result is drawn.
  const root = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    if (owed.current === null) return;
    if (pending && result.state === "none") owed.current = shown;
    if (pending && shown === owed.current) return;
    owed.current = null;
    (root.current?.querySelector<HTMLElement>(".bento-refused") ?? root.current)?.focus();
  }, [pending, shown, result.state]);

  const shared = { ref: root, id: frame.domId("foot", scope), "data-foot": "", "data-foot-owner": frame.id, tabIndex: -1, className: cn("bento-foot", className), onChange: frame.intend } as const;

  if (result.state === "confirmed" || result.state === "partial" || result.state === "unknown") {
    let lines: React.ReactNode;
    let act: React.ReactNode = null;
    if (result.state === "confirmed") {
      lines = (
        <>
          {result.effects.map((line) => (
            <li key={line}><Shape state="done" /><span>{wholeTimes(line)}</span></li>
          ))}
          {result.remains.map((line) => (
            <li key={line} data-line="remains"><Shape state="quiet" /><span>{wholeTimes(line)}</span></li>
          ))}
        </>
      );
    } else if (result.state === "partial") {
      lines = (
        <>
          {result.went.map((line) => (
            <li key={line.sentence}><Shape state="done" /><span>{wholeTimes(line.sentence)}</span></li>
          ))}
          {result.failed.map((line) => {
            const part = parts.find((p) => p.id === line.fix);
            return (
              <li key={line.sentence} data-line="failed">
                <Shape state="failed" />
                <span>
                  {wholeTimes(line.sentence)}{" "}
                  {part && <LinkAct onPress={() => frame.focus({ part: part.id })}>Go to {part.label.toLowerCase()}</LinkAct>}
                </span>
              </li>
            );
          })}
          {result.unsure?.map((line) => (
            <li key={line.sentence} data-line="unsure"><Shape state="reading" /><span>{wholeTimes(line.sentence)}</span></li>
          ))}
          {result.remains?.map((line) => (
            <li key={line} data-line="remains"><Shape state="quiet" /><span>{wholeTimes(line)}</span></li>
          ))}
        </>
      );
      // Retry resends only the failed ids, and leads when it is there; Check
      // reads the unsure ones and never sends. Both run under the commit's key.
      const { retry, check, requestId } = result;
      act = (
        <>
          {retry && (
            <>
              <ActButton act="commit" lead={lead} candidate={candidate} pending={pending} onPress={() => void press(() => retry.run(requestId), true)}>
                {retry.verb}
              </ActButton>
              <small>{wholeTimes(retry.note)}</small>
            </>
          )}
          {check && (
            <>
              <ActButton act="check" lead={!retry && lead} candidate={retry ? undefined : candidate} pending={pending} onPress={() => void press(() => check.run(requestId), true)}>
                {check.verb}
              </ActButton>
              <small>{wholeTimes(check.note)}</small>
            </>
          )}
        </>
      );
    } else {
      lines = (
        <li><Shape state="reading" /><span>{wholeTimes(result.sentence)}</span></li>
      );
      act = (
        <>
          <ActButton act="check" lead={lead} candidate={candidate} pending={pending} onPress={() => void press(result.check.run, true)}>
            {result.check.verb}
          </ActButton>
          <small>{wholeTimes(result.check.note)}</small>
        </>
      );
    }
    return (
      // A docked result can be the dialog's last focus target after Check
      // removes its button. Keep it in the tab order so the focus scope can
      // wrap Tab to Return instead of restoring this same result forever.
      <div {...shared} tabIndex={0} data-result={result.state} role="status">
        <div>
          {result.cap && <p className="bento-cap">{wholeTimes(result.cap)}</p>}
          <ul className="bento-effects">{lines}</ul>
        </div>
        {act && <div className="bento-foot-act">{act}</div>}
      </div>
    );
  }

  // While the commit runs, its own save blocks it with the typed reason (O8).
  const blockers: Blocker[] = pending ? [{ kind: "pending" }, ...blockersOf({ commit, parts })] : blockersOf({ commit, parts });
  const reasonIds = blockers.map((_, index) => frame.domId("blocker", scope, String(index)));
  return (
    <div {...shared} data-result={result.state === "refused" ? "refused" : undefined}>
      {result.state === "refused" && (
        <p className="bento-refused" role="alert" tabIndex={-1}>
          <Shape state="failed" />
          <span>
            <b>{wholeTimes(result.cap)}</b> {wholeTimes(result.sentence)}
          </span>
        </p>
      )}
      <div>
        <p className="bento-cap">{wholeTimes(commit.cap)}</p>
        <ul className="bento-effects">
          {over && (
            <li data-line="scope">
              <Shape state="quiet" />
              <span>{scopeSentence(over)}</span>
            </li>
          )}
          {over?.leftOut.map((out) => (
            <li key={out.id} data-line="left-out">
              <Shape state="quiet" />
              <span>{wholeTimes(out.sentence)}</span>
            </li>
          ))}
          {blockers.map((blocker, index) => {
            const part = parts.find((p) => p.id === blocker.part);
            return (
              // Two reasons can name one part, such as a title and a link both missing: each keeps its own line.
              <li key={reasonIds[index]} id={reasonIds[index]} data-line="block">
                <Shape state="waiting" />
                <span>
                  {wholeTimes(blockerSentence(blocker))}{" "}
                  {blocker.kind === "missing" && blocker.field
                    ? <LinkAct onPress={blocker.field.focus}>Go to {blocker.field.label.toLowerCase()}</LinkAct>
                    : part && <LinkAct onPress={() => frame.focus({ part: part.id })}>Go to {part.label.toLowerCase()}</LinkAct>}
                </span>
              </li>
            );
          })}
          {commit.effects().map((effect) => (
            <li key={effect.id}>
              {effect.optional ? (
                <label className="bento-opt">
                  <input type="checkbox" checked={effect.optional.checked} onChange={effect.optional.toggle} />
                  <span aria-hidden className="bento-opt-box" />
                  <span>{wholeTimes(effect.sentence)}</span>
                </label>
              ) : (
                <>
                  <i aria-hidden className="bento-to" />
                  <span>{wholeTimes(effect.sentence)}</span>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>
      <div className="bento-foot-act">
        <ActButton
          act="commit"
          lead={lead}
          candidate={candidate}
          blockedBy={blockers.length ? reasonIds : undefined}
          pending={pending}
          onPress={() => void press(() => commit.run(newRequestId()))}
        >
          {commit.verb}
        </ActButton>
        {commit.note && <small>{wholeTimes(commit.note)}</small>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The three kinds                                                     */
/* ------------------------------------------------------------------ */

function partLevel(level: "h2" | "h3"): "h3" | "h4" {
  return level === "h2" ? "h3" : "h4";
}

/** Fact: tells. A ready Fact's answer is its headline; opened, it shows its body. */
export function FactBox({ box }: { box: FactBoxSpec<unknown> }) {
  const frame = useFrame(box as Box);
  const read = box.read;
  // A stale read keeps its last body and fields under the head's read time and Refresh (R2).
  const value = read.status === "ready" ? { of: read.value } : read.status === "stale" ? { of: read.last } : null;
  // Open, a Fact keeps its own act in its head, drawn once: an open act has nothing left to open (CASE-047).
  const { act, blocker } = frame.open && box.act?.does !== "open" ? ownAct(box as Box, frame, "head") : { act: null, blocker: null };
  return (
    <BoxRoot box={box as Box} frame={frame}>
      {!frame.open ? (
        <Face box={box as Box} frame={frame} />
      ) : (
        <>
          <Head box={box as Box} frame={frame} act={act}>
            <FactAnswer box={box} />
          </Head>
          {blocker && <BlockedReason blocker={blocker} frame={frame} parts={[]} />}
          <OpenRead read={read} frame={frame} />
          {value && box.body && <div className="bento-part">{box.body(value.of)}</div>}
          {value && box.fields && <Fields fields={box.fields(value.of)} frame={frame} />}
        </>
      )}
    </BoxRoot>
  );
}

/**
 * An open Fact's answer, under its head as on its face: its figures, else its
 * prose, else an empty read's sentence. A Fact that never folds is never a
 * face, so this is the only place it answers (F1, CASE-023).
 */
function FactAnswer({ box }: { box: FactBoxSpec<unknown> }) {
  const read = box.read;
  if (read.status === "empty") return <p className="bento-prose">{wholeTimes(read.sentence)}</p>;
  if (read.status !== "ready") return null;
  if (box.presentation === "embedded") return <div className="text-ui text-foreground-soft">{box.figures ? box.figures(read.value).map((figure) => <span key={figure.unit} className="me-4"><b className="text-foreground tabular-nums">{figure.value}</b> {figure.unit}</span>) : box.prose ? wholeTimes(box.prose(read.value)) : null}</div>;
  if (box.figures) return <Figures figures={box.figures(read.value)} />;
  if (box.prose) return <p className="bento-prose">{wholeTimes(box.prose(read.value))}</p>;
  return null;
}

/** List: finds. Its head carries its own read's figures (R5); a row opens its own work in place (K3). */
export function ListBox({ box }: { box: ListBoxSpec<unknown, unknown> }) {
  const bento = useBento();
  const frame = useFrame(box as Box);
  // The find shows what the person types, so a page that keeps the query in
  // the address, where each change waits on a transition, never moves the
  // caret. When the page's query changes, the field shows it: a reload, Back,
  // a cleared filter or the page's echo of an edit, which it already shows (R6).
  const held = box.find?.query;
  const [typed, setTyped] = React.useState(held ?? "");
  const [drawnQuery, setDrawnQuery] = React.useState(held);
  if (held !== drawnQuery) {
    setDrawnQuery(held);
    if (held !== undefined) setTyped(held);
  }
  // Rows the person has opened onto their own content stay drawn, hidden,
  // while the row or its box is hidden, so their drafts survive (O7, K3).
  const [kept, setKept] = React.useState<ReadonlySet<string>>(() => new Set());
  // Covers' folded groups the person opened or closed, by their label (CASE-048).
  const [folds, setFolds] = React.useState<ReadonlyMap<string, boolean>>(() => new Map());
  const level = partLevel(useTileHeadingLevel());
  const read = box.read;
  const value = read.status === "ready" ? read.value : read.status === "stale" ? read.last : undefined;
  const leadRow = frame.open && frame.candidate ? candidateRow(box, (key, starts) => bento.isRowOpen(box as Box, key, starts)) : null;
  // Without an open row Task, the candidate is the act of the row the List offers (CASE-032).
  const offered = frame.open && frame.candidate && !leadRow ? offeredRow(box, (key, starts) => bento.isRowOpen(box as Box, key, starts)) : null;
  const starts = startsOpenOf(box);
  const rows = rowsOf(box).map((row) => {
    const key = box.row.key(row);
    const task = box.row.task?.(row);
    const shown = frame.open && bento.isRowOpen(box as Box, key, starts(row));
    return { row, key, task, shown };
  });
  if (import.meta.env.DEV) {
    const ids = rows.flatMap(({ task }) => task?.parts?.map((part) => part.id) ?? []);
    const repeated = ids.find((id, index) => ids.indexOf(id) !== index);
    if (repeated) throw new Error(`${box.id}: the row part id ${repeated} repeats; a row's part ids carry its key.`);
    assertLayout(box, rows.map(({ row }) => row));
  }
  const seen = rows.filter(({ key, task, shown }) => shown && holdsWork(task) && !kept.has(key)).map(({ key }) => key);
  if (seen.length > 0) setKept(new Set([...kept, ...seen]));
  const drawn = rows.filter(({ key, task }) => frame.open || (kept.has(key) && holdsWork(task)));
  const lines = groupLines(box, drawn);
  // A row opened in place onto its own work takes its List the whole row, out of a pair (P1, CASE-048).
  const rowTask = rows.some(({ row, task, shown }) => shown && Boolean(task || box.row.detail?.(row)));
  return (
    <BoxRoot box={box as Box} frame={frame} rowTask={rowTask}>
      {box.working && read.status === "ready" ? <div data-face="" tabIndex={-1} aria-label={box.label} /> : frame.open ? (
        <Head box={box as Box} frame={frame}>
          {value !== undefined && box.figures && <Figures figures={box.figures(value)} />}
          {value !== undefined && Boolean(box.find?.hidden) && <p className="bento-sub" data-filtered="">{box.find!.hidden} not shown by this filter</p>}
        </Head>
      ) : (
        <Face box={box as Box} frame={frame} />
      )}
      {/* An open List that can't read says so in its head; here is Try again, as on its face (R1, R3). */}
      {frame.open && <OpenRead read={read} frame={frame} act={wayOutOf(box as Box)} />}
      {/* A blocked offered act's reason, right under the head, as on a face (F5, CASE-045). */}
      {offered && frame.blocked && <BlockedReason blocker={frame.blocked} frame={frame} parts={[]} />}
      {frame.open && value !== undefined && box.renderTools ? <div data-list-tools="">{box.renderTools(value)}</div> : frame.open && box.find && (
        <div className="bento-find">
          <Input type="search" aria-label={box.find.placeholder} placeholder={box.find.placeholder} value={typed} onChange={(event) => {
            setTyped(event.target.value);
            box.find?.onQueryChange?.(event.target.value);
          }} />
          {box.find.facets?.map((facet) => {
            // A value with no option would show the first option as chosen.
            if (import.meta.env.DEV && !facet.options.some((option) => option.value === facet.value))
              throw new Error(`${box.id}: filter ${facet.label}'s value, ${facet.value}, isn't one of its options.`);
            const reading = facet.read?.status === "loading";
            return (
              <React.Fragment key={facet.label}>
                <NativeSelect aria-label={facet.label} value={facet.value} disabled={reading} aria-busy={reading || undefined} onChange={(event) => facet.onChange(event.target.value)}>
                  {facet.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </NativeSelect>
                {facet.read?.status === "failed" && (
                  <span className="bento-facet-read" data-read="failed">
                    {wholeTimes(facet.read.sentence)}{" "}
                    <LinkAct act="check" onPress={facet.read.retry}>Try again</LinkAct>
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
      {box.renderRows && value !== undefined ? (
        <div hidden={!frame.open} inert={!frame.open} data-list-renderer="">{box.renderRows(value)}</div>
      ) : (frame.open || drawn.length > 0) &&
        (box.table ? (
          <RowTable table={box.table} label={box.label} frame={frame} hidden={!frame.open}>
            {drawn.map((entry) => {
              const cells = box.row.cells?.(entry.row) ?? [];
              const columns = box.table!.columns;
              if (import.meta.env.DEV && (cells.length !== columns.length || cells.some((cell, index) => cell.id !== columns[index].id)))
                throw new Error(`${box.id}: row ${entry.key}'s cells don't match its columns.`);
              const titleId = frame.domId("title", entry.key);
              const work = openWork(entry);
              return (
                // Roles stay explicit: a stacked table changes its parts' display, which drops their implicit roles.
                <tbody key={entry.key} role="rowgroup" data-row={entry.key}>
                  <tr role="row">
                    <th scope="row" role="rowheader">{rowTitle(entry, titleId)}</th>
                    {cells.map((cell, index) => (
                      <td key={cell.id} role="cell" data-cell={cell.id} data-invalid={"save" in cell && cell.save.status === "refused" ? "" : undefined}>
                        {/* A stacked row names each cell's column beside it, since the head isn't drawn. */}
                        <span className="bento-cell-label">{columns[index].label}</span>
                        {"read" in cell ? (
                          cell.read
                        ) : (
                          <span className="bento-cell">
                            {cell.render(`${frame.domId("col", cell.id)} ${titleId}`, fieldAria(cell.save, frame.domId("cell", entry.key, cell.id)))}
                            <FieldSaveStatus save={cell.save} id={frame.domId("cell", entry.key, cell.id)} />
                          </span>
                        )}
                      </td>
                    ))}
                    <td role="cell">{rowEnd(entry, rowControls(entry))}</td>
                  </tr>
                  {work && (
                    <tr role="row" data-row-open="" hidden={!entry.shown}>
                      <td role="cell" colSpan={columns.length + 2}>{work}</td>
                    </tr>
                  )}
                </tbody>
              );
            })}
          </RowTable>
        ) : box.layout === "covers" ? (
          coversOf()
        ) : box.layout === "lines" ? (
          linesOf(drawn, lines)
        ) : (
          // Register clips its rows; a row task's foot must stick to the viewport instead (O4).
          // A row's title and end share a short row (box.css). Rows with their own controls take box.css's columns, which move the controls under the title in a narrow box.
          <Register rowsOnly template={box.row.controls ? "var(--bento-row-cols)" : "minmax(0, max-content) auto"} className="bento-rows overflow-visible" hidden={!frame.open}>
            {drawn.map((entry) => (
              <React.Fragment key={entry.key}>
                {lines.has(entry.key) && <li data-group="" className="bento-group">{wholeTimes(lines.get(entry.key)!)}</li>}
                <RegisterRow
                  data-row={entry.key}
                  className={!entry.shown && kept.has(entry.key) && holdsWork(entry.task) ? "hover:bg-well" : undefined}
                  detail={openWork(entry)}
                >
                  <span className="min-w-0">{rowTitle(entry)}</span>
                  {rowControls(entry)}
                  {rowEnd(entry)}
                </RegisterRow>
              </React.Fragment>
            ))}
          </Register>
        ))}
    </BoxRoot>
  );

  /** What a row opened in place: its read-only detail, its own task and that task's foot (K3). */
  function openWork({ row, key, task, shown: open }: RowEntry) {
    const detail = box.row.detail?.(row);
    const keep = kept.has(key) && holdsWork(task);
    if (!((open && (detail || task)) || keep)) return undefined;
    const holdsCandidate = leadRow?.key === key;
    return (
      <div id={frame.domId("row", key)} className="bento-row-open" data-row-open={open ? "" : undefined} tabIndex={-1} hidden={!open} onChange={frame.intend} onInput={frame.intend} onClick={frame.intend}>
        {detail}
        {task?.render?.()}
        {task?.parts?.map((part) => <PartSection key={part.id} part={part} frame={frame} level={level} hidden={!open} />)}
        {open && task && (
          <Foot
            commit={task.commit}
            result={task.result}
            parts={task.parts}
            frame={frame}
            candidate={holdsCandidate ? frame.candidate : undefined}
            lead={holdsCandidate && frame.lead}
            onConfirmed={() => bento.complete(box.id)}
            className="bento-row-foot"
            scope={`row-${key}`}
          />
        )}
      </div>
    );
  }

  function rowTitle({ row }: RowEntry, id?: string) {
    const facts = box.row.facts?.(row);
    const to = box.row.nav?.(row);
    const code = box.row.course?.(row);
    const name = (
      <>
        <span id={id} className="font-semibold">
          {/* A row's name opens its workspace: page navigation, never an act (CASE-032). */}
          {to ? <Link data-nav="" to={to} className={NAME_LINK}>{box.row.title(row)}</Link> : box.row.title(row)}
        </span>
        {facts && <span className="bento-row-facts">{wholeTimes(facts)}</span>}
      </>
    );
    if (!code) return name;
    // A course's row carries its edition tone, its signature mark, before its name (K1).
    return (
      <span className="bento-row-course">
        <RegisterMark code={code} />
        <span className="min-w-0">{name}</span>
      </span>
    );
  }

  /** The row's own controls: each edits a draft, so a change in one gives the List intent (P3). */
  function rowControls({ row }: RowEntry) {
    const controls = box.row.controls?.(row);
    return controls ? (
      <span className="bento-row-controls" onChange={frame.intend} onInput={frame.intend} onClick={frame.intend}>
        {controls}
      </span>
    ) : null;
  }

  /** The row's mark, only where it differs (K3), and its one quiet act; a table's row draws its controls before them. */
  function rowEnd(entry: RowEntry, controls?: React.ReactNode) {
    const state = rowState(entry);
    const act = rowAct(entry);
    // With nothing to draw there is no end, so a List row's controls take the row's end instead.
    if (!controls && !state && !act) return null;
    return (
      <span className="bento-row-end">
        {controls}
        {state && <StateMark state={state} />}
        {act}
      </span>
    );
  }

  /** The row's mark, only where it differs from its neighbours (K3). */
  function rowState({ row, key, task, shown: open }: RowEntry): BoxState | null | undefined {
    // A hidden row whose task keeps a browser draft says so, as a hidden box does, unless a send is owed its answer (CASE-038, CASE-041).
    const owed = unresolved(task?.result, bento.isPending(`${frame.id}\u0000row-${key}`));
    return !open && keptInTab(task?.parts) && !owed ? KEPT : box.row.state?.(row);
  }

  /** The row's one act: quiet, or the List's candidate when it's the row the List offers. */
  function rowAct({ row, key, task, shown: open }: RowEntry): React.ReactNode {
    const act = box.row.act?.(row);
    if (!act) return null;
    const toggle = () => {
      // A List opens one row's Task at a time: another open row's work is hidden, its draft kept (CASE-041, O4, O7).
      if (!open && task) for (const other of rows) if (other.key !== key && other.shown && other.task) bento.toggleRow(box.id, other.key, false);
      bento.toggleRow(box.id, key, !open);
      if (!open) frame.focus({ row: key });
    };
    // The row the List offers draws its act as the List's candidate, which can lead (P2, CASE-032).
    const offer = offered?.key === key ? offered.act : null;
    return (
      <>
        {offer && frame.candidate ? (
          frame.blocked ? (
            // Blocked, it stays the List's candidate and is described by its reason, but never fills (P2, F5).
            <span className="bento-blocked">
              <ActButton act={offer.does} candidate={frame.candidate} blockedBy={[frame.domId("blocked")]}>
                {offer.verb}
              </ActButton>
            </span>
          ) : offer.does === "visit" ? (
            <VisitAct address={offer.visit} box={box.id} next={offer.next} lead={frame.lead} candidate={frame.candidate}>
              {offer.verb}
            </VisitAct>
          ) : offer.does === "leave" ? (
            <ExitAct kind="leave" verb={offer.verb} to={offer.providerUrl} lead={frame.lead} candidate={frame.candidate} />
          ) : offer.does === "check" ? (
            <ActButton act="check" lead={frame.lead} candidate={frame.candidate} onPress={offer.run}>
              {offer.verb}
            </ActButton>
          ) : (
            <ActButton act="open" lead={frame.lead} candidate={frame.candidate} onPress={toggle}>
              {offer.verb}
            </ActButton>
          )
        ) : (
          act.does === "leave" ? (
            <ExitLink kind="leave" exit={{ label: act.verb, to: act.providerUrl, external: true }} box={box.id} onReopen={() => undefined} />
          ) : act.does === "visit" ? (
            <VisitLink address={act.visit} box={box.id} next={act.next}>
              {act.verb}
            </VisitLink>
          ) : act.does === "check" ? (
            <LinkAct act="check" onPress={act.run}>
              {act.verb}
            </LinkAct>
          ) : act.does === "exit" && act.to ? (
            // Another IMAM page is page navigation, never an exit act (CASE-032, CASE-040).
            <Link data-nav="" to={act.to} className={QUIET_LINK}>
              {act.verb}
            </Link>
          ) : (
            <LinkAct
              act={open ? "exit" : "open"}
              expanded={open}
              // What the row opened in place, while it is drawn (CASE-040).
              controls={open && (box.row.detail?.(row) || task) ? frame.domId("row", key) : undefined}
              onPress={toggle}
            >
              {open ? "Hide" : act.verb}
            </LinkAct>
          )
        )}
      </>
    );
  }

  /** The row's one destination across its width, named by its code and name; without one, its name (CASE-048). */
  function rowTarget({ row }: RowEntry, code: string | undefined, describedBy: string) {
    const to = box.row.nav?.(row);
    const visit = box.row.visit?.(row);
    const name = (
      <>
        {/* The code is drawn aria-hidden beside it, so the target says it once, first. */}
        {code && (
          <>
            <span className="sr-only">{code}</span>{" "}
          </>
        )}
        {box.row.title(row)}
      </>
    );
    if (to)
      return (
        <Link data-nav="" data-row-target="" to={to} aria-describedby={describedBy} className="bento-row-target">
          {name}
        </Link>
      );
    if (visit)
      return (
        <RowVisit address={visit} box={box.id} describedBy={describedBy}>
          {name}
        </RowVisit>
      );
    return box.row.title(row);
  }

  /** `lines`: one line per row, in columns aligned down the List (CASE-048). */
  function linesOf(entries: RowEntry[], groups: ReadonlyMap<string, string>) {
    return (
      <Register rowsOnly template="auto" className="bento-rows bento-lines overflow-visible" hidden={!frame.open}>
        {entries.map((entry) => (
          <React.Fragment key={entry.key}>
            {groups.has(entry.key) && <li data-group="" className="bento-group">{wholeTimes(groups.get(entry.key)!)}</li>}
            <RegisterRow
              data-row={entry.key}
              className={!entry.shown && kept.has(entry.key) && holdsWork(entry.task) ? "hover:bg-well" : undefined}
              detail={openWork(entry)}
            >
              {lineOf(entry)}
            </RegisterRow>
          </React.Fragment>
        ))}
      </Register>
    );
  }

  function lineOf(entry: RowEntry) {
    const code = box.row.course?.(entry.row);
    const end = box.row.end?.(entry.row);
    const state = rowState(entry);
    const said = frame.domId("end", entry.key);
    const target = rowTarget(entry, code, said);
    const targeted = typeof target !== "string";
    return (
      <div className="bento-line">
        <span className="bento-line-mark">{code && <RegisterMark code={code} />}</span>
        <RegisterCode aria-hidden={targeted || undefined}>{code}</RegisterCode>
        {/* The name and its own end cell share the rest of the line, so another row's end never narrows this name (SD-F1). */}
        <span className="bento-line-main">
          <span className="bento-line-name">{target}</span>
          <span className="bento-row-end">
            <span id={said} className="bento-line-said">
              {state && <StateMark state={state} />}
              {/* A space the flex row doesn't draw, so the target's description reads as words. */}
              {state && end && " "}
              {end && <span className="bento-line-end">{wholeTimes(end)}</span>}
            </span>
            {rowAct(entry)}
          </span>
        </span>
      </div>
    );
  }

  /** `covers`: each course an edition cover, then each group folded with its count (CASE-048). */
  function coversOf() {
    const covers = drawn.filter(({ row }) => !box.row.group?.(row));
    const grouped = drawn.filter(({ row }) => Boolean(box.row.group?.(row)));
    const codes = covers.map(({ row }) => box.row.course?.(row));
    const shared = new Set(codes.filter((code, index) => code && codes.indexOf(code) !== index));
    const labels = [...new Set(grouped.map(({ row }) => box.row.group!(row)!))];
    return (
      <div className="bento-covers-list" hidden={!frame.open}>
        {covers.length > 0 && (
          <ul className="bento-covers" aria-label={box.label}>
            {covers.map((entry) => coverOf(entry, shared))}
          </ul>
        )}
        {labels.map((label) => {
          const members = grouped.filter(({ row }) => box.row.group!(row) === label);
          // A group starts open only when no course is a cover, so a person with none sees their others (CASE-048).
          const open = folds.get(label) ?? covers.length === 0;
          return (
            <details
              key={label}
              data-fold=""
              className="bento-fold"
              open={open}
              onToggle={(event) => {
                const now = event.currentTarget.open;
                if (now !== open) setFolds(new Map(folds).set(label, now));
              }}
            >
              <summary>
                {wholeTimes(label)} <span className="bento-fold-count">{members.length}</span>
              </summary>
              {linesOf(members, new Map())}
            </details>
          );
        })}
      </div>
    );
  }

  function coverOf(entry: RowEntry, shared: ReadonlySet<string | undefined>) {
    const code = box.row.course?.(entry.row);
    const crn = code && shared.has(code) ? box.row.crn?.(entry.row) : undefined;
    const facts = box.row.facts?.(entry.row);
    const state = rowState(entry);
    const said = frame.domId("end", entry.key);
    const act = rowAct(entry);
    return (
      <li key={entry.key} data-row={entry.key} data-cover="" className="bento-cover">
        {code && <EditionCover code={code} label={crn ? `CRN ${crn}` : undefined} />}
        <div className="bento-cover-plane">
          <p className="bento-cover-title">{rowTarget(entry, [code, crn && `CRN ${crn}`].filter(Boolean).join(" ") || undefined, said)}</p>
          <div id={said}>
            {facts && <p className="bento-cover-facts">{wholeTimes(facts)}</p>}
            {facts && state && " "}
            {state && <StateMark state={state} />}
          </div>
          {act && <div className="bento-cover-act">{act}</div>}
        </div>
      </li>
    );
  }
}

/** A row whose one destination is a visit: its name, pressed anywhere across the row, with no act word (O10, CASE-048). */
function RowVisit({ address, box, describedBy, children }: { address: VisitAddress; box: string; describedBy: string; children: React.ReactNode }) {
  const press = useVisitPress(address, box);
  return (
    <button
      type="button"
      data-act="visit"
      data-visit-to={address}
      data-row-target=""
      aria-describedby={describedBy}
      aria-disabled={press === null || undefined}
      className="bento-row-target"
      onClick={press ?? undefined}
    >
      {children}
    </button>
  );
}

/** In development, a List's layout fields name the layout that draws them (CASE-048). */
function assertLayout(box: ListBoxSpec<unknown, unknown>, rows: unknown[]) {
  if (box.layout && box.table) throw new Error(`${box.id}: a table draws its own columns, so it takes no layout.`);
  if (box.layout === "covers" && box.fold !== "never") throw new Error(`${box.id}: covers are a Home's List, always open (fold "never").`);
  if (box.home && box.fold !== "never") throw new Error(`${box.id}: a Home's List is always open (fold "never").`);
  for (const row of rows) {
    const key = box.row.key(row);
    if (box.row.nav?.(row) && box.row.visit?.(row)) throw new Error(`${box.id}: row ${key} has one destination, its nav or its visit, not both.`);
    if (!box.layout && (box.row.visit?.(row) || box.row.end?.(row) || box.row.crn?.(row)))
      throw new Error(`${box.id}: row ${key}'s visit, end or CRN is drawn only by the lines or covers layout.`);
    // A covers List's grouped rows are drawn as lines too.
    const line = box.layout === "lines" || (box.layout === "covers" && Boolean(box.row.group?.(row)));
    if (line && box.row.facts?.(row)) throw new Error(`${box.id}: a line draws no facts; row ${key}'s belong in its end cell or its group line.`);
  }
}

interface RowEntry {
  row: unknown;
  key: string;
  task: RowTask | undefined;
  /** Whether the row is open in its open box. */
  shown: boolean;
}

/**
 * The group line each row starts, by its key: where its group differs from
 * the row above's (A1 C27, L05). The kit never sorts, so a group split by
 * another or a row with no group after a line is the area's order to fix.
 */
function groupLines(box: ListBoxSpec<unknown, unknown>, entries: RowEntry[]): ReadonlyMap<string, string> {
  const lines = new Map<string, string>();
  if (!box.row.group) return lines;
  if (import.meta.env.DEV && box.table) throw new Error(`${box.id}: group lines are drawn between a List's rows, not in a table.`);
  const seen = new Set<string>();
  let last: string | undefined;
  for (const { row, key } of entries) {
    const group = box.row.group(row);
    if (import.meta.env.DEV && last !== undefined && group === undefined) throw new Error(`${box.id}: row ${key} has no group but follows ${last}; rows with no group come first.`);
    if (group !== undefined && group !== last) {
      if (import.meta.env.DEV && seen.has(group)) throw new Error(`${box.id}: the group ${group} is split; its rows sit together.`);
      seen.add(group);
      lines.set(key, group);
    }
    last = group;
  }
  return lines;
}

/** The least widths a table keeps, in rem: the rows' own column, each cell, and the row's mark and act. */
const TABLE_LEAST = { title: 11, cell: 7, end: 9 };

/**
 * A List read as a table (O9): its columns' heads, then one `tbody` per row, so
 * a row's cells and the work it opens stay one `data-row`. A table that can't
 * keep its least widths in its box stacks each row's cells instead. Nothing
 * scrolls sideways: a scroll box would also hold a row task's foot, which
 * must stay on the viewport (O4).
 */
function RowTable({ table, label, frame, hidden, children }: { table: NonNullable<ListBoxSpec<unknown, unknown>["table"]>; label: string; frame: Frame; hidden: boolean; children: React.ReactNode }) {
  const root = React.useRef<HTMLDivElement>(null);
  const stacked = useStacked(root, table.columns.length);
  return (
    <div ref={root} className="bento-table" hidden={hidden} data-stacked={stacked ? "" : undefined}>
      {table.note && <p className="bento-table-note">{wholeTimes(table.note)}</p>}
      {/* Named as its box is, so the table can be found and announced by itself. */}
      <table role="table" aria-label={label} onKeyDown={moveBetweenCells}>
        <thead role="rowgroup">
          <tr role="row">
            <th scope="col" role="columnheader">{table.title}</th>
            {table.columns.map((column) => (
              <th key={column.id} scope="col" role="columnheader">
                <span id={frame.domId("col", column.id)}>{column.label}</span>
                {column.sub && <small>{wholeTimes(column.sub)}</small>}
              </th>
            ))}
            <td role="cell" />
          </tr>
        </thead>
        {children}
      </table>
    </div>
  );
}

/** Whether a table's box is narrower than its least widths, so its rows stack. */
function useStacked(root: React.RefObject<HTMLElement | null>, columns: number): boolean {
  const [stacked, setStacked] = React.useState(false);
  React.useLayoutEffect(() => {
    const element = root.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const least = (TABLE_LEAST.title + columns * TABLE_LEAST.cell + TABLE_LEAST.end) * rem;
    // A hidden table measures 0; it is measured again once it is shown.
    const measure = () => {
      if (element.clientWidth > 0) setStacked(element.clientWidth < least);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [root, columns]);
  return stacked;
}

/**
 * Whether a head's label, mark, owner and Hide can't share one line at their
 * own widths. Only then does the mark go under the label (CSS), so a head that
 * fits keeps its mark beside the label, and Hide stays whole on the first line.
 * The widths are read in one forced layout with nothing wrapping, before paint.
 */
function useCrowded(line: React.RefObject<HTMLElement | null>): boolean {
  const [crowded, setCrowded] = React.useState(false);
  const measure = React.useCallback(() => {
    const element = line.current;
    const header = element?.querySelector<HTMLElement>('[data-slot="tile-header"]');
    // A hidden head measures 0; it is measured again once it is shown.
    if (!element || !header || element.clientWidth === 0) return;
    element.setAttribute("data-measure", "");
    const items = Array.from(header.children);
    const gap = parseFloat(getComputedStyle(header).columnGap) || 0;
    const needed = items.reduce((sum, item) => sum + item.getBoundingClientRect().width, 0) + gap * (items.length - 1);
    const room = element.clientWidth;
    element.removeAttribute("data-measure");
    setCrowded(needed > room + 0.5);
  }, [line]);
  // Its words change with its state, so it measures after every render as well as on resize.
  React.useLayoutEffect(measure);
  React.useLayoutEffect(() => {
    const element = line.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [line, measure]);
  return crowded;
}

/**
 * Moves focus between a table's cells (O9): Enter and the arrows go down or up
 * the column, Shift Enter goes up, and Left or Right cross to the next cell at
 * the field's edge. Leaving a cell is the blur that saves it. Where there's no
 * cell to go to, the key stays with the control.
 */
function moveBetweenCells(event: React.KeyboardEvent<HTMLTableElement>) {
  const field = event.target;
  if (!(field instanceof HTMLInputElement) || event.nativeEvent.isComposing) return;
  // A modified key is the field's or the browser's own; Shift with an arrow extends the selection.
  if (event.altKey || event.ctrlKey || event.metaKey || (event.shiftKey && event.key !== "Enter")) return;
  const cell = field.closest<HTMLElement>("td[data-cell]");
  const line = cell?.parentElement;
  if (!cell || !line || cell.closest("table") !== event.currentTarget) return;
  const lines = [...event.currentTarget.querySelectorAll<HTMLElement>(":scope > tbody > tr:first-child")];
  const cellsOf = (row: Element | undefined) => (row ? [...row.querySelectorAll<HTMLElement>(":scope > td[data-cell]")] : []);
  const across = cellsOf(line);
  const row = lines.indexOf(line);
  const column = across.indexOf(cell);
  // A field that holds no caret, such as a checkbox, is always at its edge.
  const caret = field.selectionStart;
  const atStart = caret === null || (caret === 0 && field.selectionEnd === 0);
  const atEnd = caret === null || caret === field.value.length;
  let target: HTMLElement | undefined;
  if (event.key === "Enter") target = cellsOf(lines[event.shiftKey ? row - 1 : row + 1])[column];
  else if (event.key === "ArrowDown") target = cellsOf(lines[row + 1])[column];
  else if (event.key === "ArrowUp") target = cellsOf(lines[row - 1])[column];
  else if (event.key === "ArrowLeft" && atStart) target = across[column - 1];
  else if (event.key === "ArrowRight" && atEnd) target = across[column + 1];
  const next = target?.querySelector<HTMLElement>("input, select, textarea, button");
  if (!next) return;
  event.preventDefault();
  next.focus();
}

/** Task: changes. Its parts in time order, then one foot that commits once (K2, O2, O3). */
export function TaskBox({ box }: { box: TaskBoxSpec<unknown> }) {
  const bento = useBento();
  const dock = React.useContext(VisitTaskFoot);
  const frame = useFrame(box as Box);
  const level = partLevel(useTileHeadingLevel());
  const [visited, setVisited] = React.useState(frame.open);
  if (frame.open && !visited) setVisited(true);
  // A read that isn't ready or stale draws no editable parts and no foot: an
  // unavailable record never looks actionable (R1, R3, CASE-005).
  const resolved = box.read.status === "ready" || box.read.status === "stale";
  const drawn = frame.open && resolved;
  // Parts the person has seen stay drawn, hidden, while the box is hidden or
  // its read is unresolved, so their drafts survive (O7). They hold the same
  // place in the box whether it is open or not. A confirmed box that folds
  // draws its result alone; one that never folds keeps its fields under the
  // result, so the next edit can start again (CASE-015).
  const parts = box.result.state === "confirmed" && box.fold !== "never" ? [] : box.parts.filter((part) => drawn || (visited && part.draft !== "none"));
  const foot = drawn ? <Foot commit={box.commit} result={box.result} parts={box.parts} frame={frame} candidate={frame.candidate} lead={frame.lead} onConfirmed={() => bento.complete(box.id)} scope="foot" over={box.over} /> : null;
  const destination = box.footer === "host" && dock?.activeBoxIds.has(box.id) ? dock.target : null;
  return (
    <BoxRoot box={box as Box} frame={frame}>
      {frame.open ? <Head box={box as Box} frame={frame} /> : <Face box={box as Box} frame={frame} />}
      {/* A Task's own check reads its result, and its foot draws that. */}
      {frame.open && !resolved && <OpenRead read={box.read} frame={frame} />}
      {parts.map((part) => (
        <PartSection key={part.id} part={part} frame={frame} level={level} hidden={!drawn} />
      ))}
      {destination && foot ? createPortal(foot, destination, box.id) : foot}
    </BoxRoot>
  );
}

/**
 * A face act that is a way out of an empty or unread read, which an open box
 * keeps: it reads again, or it leaves for the provider, such as Connect
 * Classroom before anything is read (R1, C5).
 */
type WayOut = Extract<FaceAct, { does: "check" | "leave" }>;

function wayOutOf(box: Box): WayOut | undefined {
  return box.act?.does === "check" || box.act?.does === "leave" ? box.act : undefined;
}

/**
 * An open box whose read isn't resolved: its head already says the read's
 * state; here is how long, or Try again (R1, R3). A box whose own act reads
 * again keeps it here while ready, empty or unread, and one that leaves for
 * the provider while empty or unread; a failed or stale read's Try again or
 * Refresh stands in for either, so one is drawn. No unready read leads (P2).
 * An open Fact keeps its act in its head instead (CASE-047).
 */
function OpenRead({ read, frame, act }: { read: Read<unknown>; frame: Frame; act?: WayOut }) {
  const unready = read.status === "empty" || read.status === "unread";
  let again: React.ReactNode = null;
  if (act?.does === "check" && (read.status === "ready" || unready))
    again = <ActButton act="check" onPress={() => { act.run?.(); frame.focus("head"); }}>{act.verb}</ActButton>;
  else if (act?.does === "leave" && unready)
    again = act.run ? (
      <LeaveRun verb={act.verb} run={act.run} lead={false} candidate={undefined} />
    ) : (
      <ExitLink kind="leave" exit={{ label: act.verb, to: act.providerUrl }} box={frame.id} onReopen={() => frame.openBox()} />
    );
  if (read.status === "loading")
    return (
      <p className="bento-part">
        <span className="bento-skel" style={{ width: "60%" }} />
      </p>
    );
  if (read.status === "failed")
    return (
      <div className="bento-part">
        <ActButton act="check" onPress={() => { read.retry(); frame.focus("head"); }}>Try again</ActButton>
      </div>
    );
  const sub = (read.status === "empty" || read.status === "unread") && read.sub ? wholeTimes(read.sub) : null;
  if (!again) return sub ? <p className="bento-part bento-detail">{sub}</p> : null;
  return (
    <div className="bento-part">
      {sub && <p className="bento-detail">{sub}</p>}
      {again}
    </div>
  );
}

/**
 * A Fact's fields (CASE-026): each is edited where it's drawn and says its own
 * save word there. A field isn't an act, and a newer version is chosen at the
 * field it changed (R4).
 */
function Fields({ fields, frame }: { fields: FactField[]; frame: Frame }) {
  return (
    <div className="bento-fields" onChange={frame.edit} onInput={frame.edit} onClick={frame.edit}>
      {fields.map((field) => {
        const id = frame.domId("field", field.id);
        return (
          <div key={field.id} className="bento-field" data-field={field.id} data-conflict={String(Boolean(field.conflict))}>
            <span id={`${id}-label`} className="bento-field-label">{field.label}</span>
            {field.conflict && (
              <ConflictNotice
                object={field.label.toLowerCase()}
                message={wholeTimes(field.conflict.sentence)}
                facts={wholeFacts(field.conflict.facts)}
                keepMineLabel={field.conflict.keepMine.label}
                loadTheirsLabel={field.conflict.loadNewer.label}
                onKeepMine={field.conflict.keepMine.run}
                onLoadTheirs={field.conflict.loadNewer.run}
              />
            )}
            <div className="bento-field-value">
              {field.render(`${id}-label`, fieldAria(field.save, id))}
              <FieldSaveStatus save={field.save} id={id} />
            </div>
            {field.note && <p className="bento-field-note">{wholeTimes(field.note)}</p>}
          </div>
        );
      })}
    </div>
  );
}

/** A refused value is invalid, and its reason describes it (O3). */
function fieldAria(save: FieldSave, id: string): FieldAria {
  return save.status === "refused" ? { "aria-invalid": true, "aria-describedby": `${id}-reason` } : {};
}

/**
 * A value the person can change back in place saves in its own field and
 * says so there: Saving, Saved, Not saved with the server's reason, or Not
 * confirmed with Check when no answer came back (O3). Never a toast, and never
 * the foot.
 */
export function FieldSaveStatus({ save, id }: { save: FieldSave; id: string }) {
  const root = React.useRef<HTMLSpanElement>(null);
  // Check and Retry leave with their answer: the person stays on the field they act for (P5).
  const thenField = (run: () => void) => {
    const field = root.current?.closest("[data-field], td[data-cell]")?.querySelector<HTMLElement>("input, select, textarea");
    run();
    field?.focus();
  };
  const status =
    save.status === "saving"
      ? ({ kind: "saving" } as const)
      : save.status === "saved"
        ? ({ kind: "saved" } as const)
        : save.status === "refused" || save.status === "failed"
          ? // Only a save that never reached the server is worth sending again.
            ({ kind: "failed", reason: save.sentence, retry: save.status === "failed" } as const)
          : null;
  return (
    <span ref={root} className="inline-flex flex-wrap items-center gap-x-2">
      {/* Here from the start, so "Not confirmed" is announced when it comes; empty, it takes no room. */}
      <span role="status" className="text-meta text-muted-foreground empty:-ml-2">
        {save.status === "unknown" ? "Not confirmed" : null}
      </span>
      {save.status === "unknown" && (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="px-0"
          // It may have saved: Check reads it, and nothing offers to save it again.
          onClick={() => thenField(save.check)}
        >
          Check
        </Button>
      )}
      <SaveStatus status={status} onRetry={() => (save.status === "failed" ? thenField(save.retry) : undefined)} />
      {/* Here from the start too, so the reason is announced after "Not saved", not only shown; it also describes the field. */}
      <span id={`${id}-reason`} role="status" className="text-meta text-destructive empty:-ml-2">
        {save.status === "refused" || save.status === "failed" ? wholeTimes(save.sentence) : null}
      </span>
    </span>
  );
}
